import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Button, Box, IconButton,
  Collapse, Divider, Grid, Container
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import logo from '../assets/VforU.png';
import { BRAND_PHONE, BRAND_PHONE_TEL, BRAND_WHATSAPP } from '../seo/SEO';

const NAV_LINKS = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  {
    label: 'Services', children: [
      { label: 'Certification Consultancy', path: '/services/certification' },
      { label: 'IT Solutions', path: '/services/it-solutions' },
      { label: 'Energy Consultancy', path: '/services/energy' },
    ]
  },
  { label: 'Contact', path: '/contact' },
];

export function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location]);

  // Prevent body scroll when menu open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [mobileOpen]);

  return (
    <>
      {/* Top bar — desktop only */}
      <Box sx={{
        backgroundColor: '#0A1628',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        display: { xs: 'none', md: 'block' },
      }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.8 }}>
            <Box sx={{ display: 'flex', gap: 3 }}>
              {[
                { icon: <PhoneIcon sx={{ fontSize: 13 }} />, text: BRAND_PHONE },
                { icon: <EmailIcon sx={{ fontSize: 13 }} />, text: 'info@vforuglobal.com' },
              ].map((item, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.7, color: '#94A3B8' }}>
                  {item.icon}
                  <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>{item.text}</Typography>
                </Box>
              ))}
            </Box>
            <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>Mon–Sat 9AM–6PM</Typography>
          </Box>
        </Container>
      </Box>

      {/* Main Navbar */}
      <AppBar position="sticky" elevation={0} sx={{
        backgroundColor: '#fff',
        borderBottom: '1px solid #E2E8F0',
        zIndex: 1200,
      }}>
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between', py: 1.2 }}>

            {/* Logo + Company Name */}
            <Box component={Link} to="/" sx={{
              textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 1.5,
            }}>
              <Box
                component="img"
                src={logo}
                alt="VforU Global Logo"
                sx={{ width: 100, height: 100, objectFit: 'contain' }}
              />
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 22, color: '#0A1628', lineHeight: 1.15 }}>
                  VforU Global
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#64748B', letterSpacing: 1.5, textTransform: 'uppercase' }}>
                  Consultancy & Solutions
                </Typography>
              </Box>
            </Box>

            {/* Desktop Nav */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0 }}>
              {NAV_LINKS.map((item) =>
                item.children ? (
                  <Box key={item.label} sx={{
                    position: 'relative',
                    '&:hover .dropdown': { opacity: 1, visibility: 'visible', transform: 'translateY(0)' }
                  }}>
                    <Button sx={{
                      color: '#334155', textTransform: 'none', fontSize: 16, fontWeight: 500,
                      px: 2, py: 1.5, borderRadius: 0,
                      borderBottom: '2px solid transparent',
                      '&:hover': { color: '#0A1628', backgroundColor: 'transparent', borderBottom: '2px solid #0A1628' },
                      transition: 'all 0.2s',
                    }}>
                      {item.label}
                      <Box component="span" sx={{ ml: 0.5, fontSize: 10, color: '#94A3B8' }}>▾</Box>
                    </Button>
                    <Box className="dropdown" sx={{
                      opacity: 0, visibility: 'hidden',
                      transform: 'translateY(8px)',
                      transition: 'all 0.2s ease',
                      position: 'absolute', top: 'calc(100% + 1px)', left: 0,
                      backgroundColor: '#fff',
                      border: '1px solid #E2E8F0',
                      borderTop: '2px solid #0A1628',
                      borderRadius: '0 0 8px 8px',
                      minWidth: 260,
                      boxShadow: '0 16px 40px rgba(0,0,0,0.1)',
                      zIndex: 999, py: 1,
                    }}>
                      {item.children.map((child) => (
                        <Button key={child.path} component={Link} to={child.path} sx={{
                          display: 'flex', width: '100%', textAlign: 'left', justifyContent: 'flex-start',
                          color: isActive(child.path) ? '#0A1628' : '#475569',
                          textTransform: 'none', fontSize: 14,
                          fontWeight: isActive(child.path) ? 600 : 400,
                          px: 2.5, py: 1.2,
                          borderLeft: isActive(child.path) ? '2px solid #0A1628' : '2px solid transparent',
                          borderRadius: 0,
                          '&:hover': { backgroundColor: '#F8FAFC', color: '#0A1628', borderLeft: '2px solid #0A1628' },
                          transition: 'all 0.15s',
                        }}>{child.label}</Button>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Button key={item.path} component={Link} to={item.path} sx={{
                    color: isActive(item.path) ? '#0A1628' : '#334155',
                    textTransform: 'none', fontSize: 16,
                    fontWeight: isActive(item.path) ? 600 : 500,
                    px: 2, py: 1.5, borderRadius: 0,
                    borderBottom: isActive(item.path) ? '2px solid #0A1628' : '2px solid transparent',
                    '&:hover': { color: '#0A1628', backgroundColor: 'transparent', borderBottom: '2px solid #0A1628' },
                    transition: 'all 0.2s',
                  }}>{item.label}</Button>
                )
              )}
            </Box>

            {/* Mobile hamburger button */}
            <IconButton
              onClick={() => setMobileOpen(!mobileOpen)}
              sx={{
                display: { md: 'none' },
                color: '#0A1628',
                width: 42, height: 42,
                borderRadius: 1.5,
                border: '1px solid #E2E8F0',
                transition: 'all 0.2s',
                '&:hover': { backgroundColor: '#F8FAFC' },
              }}>
              {mobileOpen ? <CloseIcon sx={{ fontSize: 20 }} /> : <MenuIcon sx={{ fontSize: 20 }} />}
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Full Screen Menu Overlay */}
      <Box sx={{
        display: { md: 'none' },
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: '#fff',
        zIndex: 1100,
        transform: mobileOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        overflowY: 'auto',
      }}>

        {/* Menu Header */}
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          px: 3, py: 2.5,
          borderBottom: '1px solid #E2E8F0',
          position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1,
        }}>
          <Typography component={Link} to="/" onClick={() => setMobileOpen(false)}
            sx={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              component="img"
              src={logo}
              alt="VforU Global Logo"
              sx={{ width: 44, height: 44, borderRadius: '8px', objectFit: 'contain' }}
            />
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#0A1628', lineHeight: 1.1 }}>VforU Global</Typography>
              <Typography sx={{ fontSize: 9, color: '#94A3B8', letterSpacing: 1.5, textTransform: 'uppercase' }}>Consultancy & Solutions</Typography>
            </Box>
          </Typography>
          <IconButton onClick={() => setMobileOpen(false)} sx={{ color: '#0A1628', border: '1px solid #E2E8F0', borderRadius: 1.5, width: 40, height: 40 }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* Menu Links */}
        <Box sx={{ px: 3, py: 3 }}>

          {/* Home */}
          <Box component={Link} to="/" onClick={() => setMobileOpen(false)}
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              py: 2, textDecoration: 'none',
              borderBottom: '1px solid #F1F5F9',
            }}>
            <Typography sx={{ fontSize: 17, fontWeight: isActive('/') ? 700 : 500, color: isActive('/') ? '#0A1628' : '#334155' }}>
              Home
            </Typography>
            {isActive('/') && <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#0A1628' }} />}
          </Box>

          {/* About */}
          <Box component={Link} to="/about" onClick={() => setMobileOpen(false)}
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              py: 2, textDecoration: 'none',
              borderBottom: '1px solid #F1F5F9',
            }}>
            <Typography sx={{ fontSize: 17, fontWeight: isActive('/about') ? 700 : 500, color: isActive('/about') ? '#0A1628' : '#334155' }}>
              About
            </Typography>
            {isActive('/about') && <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#0A1628' }} />}
          </Box>

          {/* Services accordion */}
          <Box sx={{ borderBottom: '1px solid #F1F5F9' }}>
            <Box onClick={() => setServicesOpen(!servicesOpen)}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2, cursor: 'pointer' }}>
              <Typography sx={{ fontSize: 17, fontWeight: 500, color: '#334155' }}>Services</Typography>
              <Box sx={{ transition: 'transform 0.25s', transform: servicesOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                <ExpandMore sx={{ color: '#94A3B8', fontSize: 22 }} />
              </Box>
            </Box>
            <Collapse in={servicesOpen}>
              <Box sx={{ pb: 2, pl: 1 }}>
                {NAV_LINKS[2].children.map((child) => (
                  <Box key={child.path} component={Link} to={child.path}
                    onClick={() => setMobileOpen(false)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 2,
                      py: 1.8, textDecoration: 'none',
                      borderBottom: '1px solid #F8FAFC',
                    }}>
                    <Box sx={{ width: 3, height: 20, backgroundColor: isActive(child.path) ? '#0A1628' : '#E2E8F0', borderRadius: 2, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 15, color: isActive(child.path) ? '#0A1628' : '#475569', fontWeight: isActive(child.path) ? 600 : 400 }}>
                      {child.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Box>

          {/* Contact */}
          <Box component={Link} to="/contact" onClick={() => setMobileOpen(false)}
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              py: 2, textDecoration: 'none',
              borderBottom: '1px solid #F1F5F9',
            }}>
            <Typography sx={{ fontSize: 17, fontWeight: isActive('/contact') ? 700 : 500, color: isActive('/contact') ? '#0A1628' : '#334155' }}>
              Contact
            </Typography>
            {isActive('/contact') && <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#0A1628' }} />}
          </Box>

          {/* CTA Button */}
          <Button component={Link} to="/contact" onClick={() => setMobileOpen(false)}
            variant="contained" fullWidth endIcon={<ArrowForwardIcon />}
            sx={{
              mt: 4, backgroundColor: '#0A1628',
              textTransform: 'none', fontWeight: 600, fontSize: 15,
              py: 1.8, borderRadius: 1.5, boxShadow: 'none',
              '&:hover': { backgroundColor: '#1E3A5F' },
            }}>
            Get a Free Quote
          </Button>

          {/* Contact info */}
          <Box sx={{ mt: 4, p: 3, borderRadius: 2, backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
            <Typography sx={{ fontSize: 11, color: '#94A3B8', fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', mb: 2 }}>
              Quick Contact
            </Typography>
            {[
              { icon: <PhoneIcon sx={{ fontSize: 16, color: '#0A1628' }} />, text: BRAND_PHONE, href: BRAND_PHONE_TEL },
              { icon: <EmailIcon sx={{ fontSize: 16, color: '#0A1628' }} />, text: 'info@vforuglobal.com', href: 'mailto:info@vforuglobal.com' },
              { icon: <WhatsAppIcon sx={{ fontSize: 16, color: '#25D366' }} />, text: 'WhatsApp Us', href: BRAND_WHATSAPP },
            ].map((item, i) => (
              <Box key={i} component="a" href={item.href}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  py: 1.2, textDecoration: 'none',
                  borderBottom: i < 2 ? '1px solid #E2E8F0' : 'none',
                }}>
                {item.icon}
                <Typography sx={{ fontSize: 14, color: '#334155', fontWeight: 500 }}>{item.text}</Typography>
              </Box>
            ))}
          </Box>

          {/* Business hours */}
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>
              Mon – Sat &nbsp;·&nbsp; 9:00 AM – 6:00 PM
            </Typography>
          </Box>
        </Box>
      </Box>

      <ScrollToTop />

      {/* WhatsApp Float */}
      <IconButton component="a" href={BRAND_WHATSAPP} target="_blank" sx={{
        position: 'fixed', bottom: 24, right: 24, zIndex: 1000,
        backgroundColor: '#25D366', color: '#fff', width: 52, height: 52,
        boxShadow: '0 4px 20px rgba(37,211,102,0.4)',
        '&:hover': { backgroundColor: '#22C55E', transform: 'scale(1.08)' },
        transition: 'all 0.25s ease',
      }}>
        <WhatsAppIcon sx={{ fontSize: 24 }} />
      </IconButton>
    </>
  );
}

function ScrollToTop() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const handleScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  if (!visible) return null;
  return (
    <IconButton onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} sx={{
      position: 'fixed', bottom: 84, right: 24, zIndex: 1000,
      backgroundColor: '#0A1628', color: '#fff', width: 40, height: 40,
      boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
      '&:hover': { backgroundColor: '#1E3A5F' },
      transition: 'all 0.25s ease',
    }}>
      <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
    </IconButton>
  );
}

export function Footer() {
  return (
    <Box sx={{ backgroundColor: '#0A1628', color: '#E2E8F0', pt: 8, pb: 4 }}>
      <Container maxWidth="lg">
        <Grid container spacing={6}>

          {/* Brand */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box
                component="img"
                src={logo}
                alt="VforU Global Logo"
                sx={{ width: 56, height: 56, borderRadius: '8px', objectFit: 'contain', backgroundColor: '#fff', p: 0.5 }}
              />
            </Box>
            <Typography sx={{ fontSize: 14, color: '#64748B', lineHeight: 1.9, mb: 3, maxWidth: 280 }}>
              Your trusted partner for Certification, IT Solutions, and Energy Consultancy. Helping Indian businesses grow with compliance, technology, and sustainability.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {[
                { icon: <WhatsAppIcon sx={{ fontSize: 18 }} />, href: BRAND_WHATSAPP },
                { icon: <EmailIcon sx={{ fontSize: 18 }} />, href: 'mailto:info@vforuglobal.com' },
              ].map((item, i) => (
                <IconButton key={i} component="a" href={item.href} target="_blank" sx={{
                  color: '#64748B', border: '1px solid #1E293B',
                  width: 34, height: 34, borderRadius: 1,
                  '&:hover': { color: '#fff', borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.05)' },
                  transition: 'all 0.2s',
                }}>{item.icon}</IconButton>
              ))}
            </Box>
          </Grid>

          {/* Services */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography sx={{ fontWeight: 600, fontSize: 11, color: '#475569', letterSpacing: 2, textTransform: 'uppercase', mb: 3 }}>Services</Typography>
            {[
              { label: 'Certification Consultancy', path: '/services/certification' },
              { label: 'IT Solutions', path: '/services/it-solutions' },
              { label: 'Energy Consultancy', path: '/services/energy' },
            ].map((item) => (
              <Typography key={item.path} component={Link} to={item.path} sx={{
                display: 'block', color: '#94A3B8', fontSize: 14, textDecoration: 'none',
                mb: 1.5, '&:hover': { color: '#fff' }, transition: 'color 0.2s',
              }}>{item.label}</Typography>
            ))}
          </Grid>

          {/* Company */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography sx={{ fontWeight: 600, fontSize: 11, color: '#475569', letterSpacing: 2, textTransform: 'uppercase', mb: 3 }}>Company</Typography>
            {[
              { label: 'Home', path: '/' },
              { label: 'About Us', path: '/about' },
              { label: 'Contact', path: '/contact' },
            ].map((item) => (
              <Typography key={item.path} component={Link} to={item.path} sx={{
                display: 'block', color: '#94A3B8', fontSize: 14, textDecoration: 'none',
                mb: 1.5, '&:hover': { color: '#fff' }, transition: 'color 0.2s',
              }}>{item.label}</Typography>
            ))}
          </Grid>

          {/* Contact */}
          <Grid item xs={12} md={3}>
            <Typography sx={{ fontWeight: 600, fontSize: 11, color: '#475569', letterSpacing: 2, textTransform: 'uppercase', mb: 3 }}>Contact</Typography>
            {[
              { icon: <PhoneIcon sx={{ fontSize: 15 }} />, text: BRAND_PHONE },
              { icon: <EmailIcon sx={{ fontSize: 15 }} />, text: 'info@vforuglobal.com' },
              { icon: <LocationOnIcon sx={{ fontSize: 15 }} />, text: 'Chennai, Tamil Nadu, India' },
            ].map((item, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                <Box sx={{ color: '#475569', mt: 0.3, flexShrink: 0 }}>{item.icon}</Box>
                <Typography sx={{ fontSize: 14, color: '#94A3B8', lineHeight: 1.6 }}>{item.text}</Typography>
              </Box>
            ))}
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: '#1E293B', my: 5 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Typography sx={{ fontSize: 13, color: '#334155' }}>© {new Date().getFullYear()} VforU Global. All rights reserved.</Typography>
          <Typography sx={{ fontSize: 13, color: '#334155' }}>Certification · IT Solutions · Energy Consultancy</Typography>
        </Box>
      </Container>
    </Box>
  );
}